import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { GetHelpDialog } from "@/components/app/support/get-help-dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { RequestRow } from "./-components/request-row";

export const Route = createFileRoute("/_authenticated/_support/help/")({
  head: () => ({ meta: [{ title: "Get help | People AI" }] }),
  component: HelpPage,
});

function HelpPage() {
  const [asking, setAsking] = useState(false);
  const requests = useQuery(orpc.support.requests.mine.queryOptions());
  const rows = requests.data ?? [];
  const open = rows.filter((row) => row.status === "open").length;
  const ask = <Button onClick={() => setAsking(true)}>Send a request</Button>;

  return (
    <>
      <PageHeader
        title="Get help"
        meta={
          requests.isPending
            ? "Loading…"
            : rows.length === 0
              ? "Ask the People AI team anything. They reply by email."
              : `${rows.length} request${rows.length === 1 ? "" : "s"}, ${open} open`
        }
        actions={rows.length > 0 ? ask : null}
      />
      {requests.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : requests.isError ? (
        <ReadFailed
          message="Couldn't load your requests."
          error={requests.error}
          onRetry={() => void requests.refetch()}
          retrying={requests.isRefetching}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No requests yet"
          description="Something not working, a question, or an idea? Send it here and the team replies by email."
          action={ask}
        />
      ) : (
        <ul className="bg-card shadow-card overflow-hidden rounded-xl border">
          {rows.map((request) => (
            <RequestRow key={request.id} request={request} />
          ))}
        </ul>
      )}
      <GetHelpDialog open={asking} onOpenChange={setAsking} />
    </>
  );
}
